import classNames from "classnames";
import * as React from "react";

import { Info, Scissors } from "@/shared/foundation/icons";
import { StatusBadge } from "@/shared/foundation/primitives";

import { CodeBlock } from "./CodeBlock";
import styles from "./RunPythonOutput.module.css";
import {
  type LogLine,
  type LogLineKind,
  parseRunPythonLogs,
  type RunPythonPayload,
  stripLogOffsets,
} from "./runPythonPayload";

const LINE_CLASS: Record<LogLineKind, string | undefined> = {
  plain: undefined,
  traceback: styles.tracebackLine,
  frame: styles.frameLine,
  marker: styles.markerLine,
  error: styles.errorLine,
};

interface RunPythonOutputProps {
  payload: RunPythonPayload;
}

/**
 * What a finished `run_python` call printed: the final exception, the server's note, then the log
 * with run offsets in a gutter.
 */
export function RunPythonOutput({
  payload,
}: RunPythonOutputProps): React.JSX.Element {
  const lines = React.useMemo(
    () => parseRunPythonLogs(payload.logs),
    [payload.logs],
  );
  // The last one: in a chained traceback, the exception the run died on.
  const errorLine = [...lines].reverse().find((line) => line.kind === "error");

  return (
    <div className={styles.container}>
      {errorLine && (
        <p className={styles.errorSummary} title={errorLine.text}>
          <span className={styles.errorSummaryText}>{errorLine.text}</span>
        </p>
      )}

      {payload.note && (
        <p className={styles.note}>
          <Info aria-hidden className={styles.noteIcon} />
          <span>{payload.note}</span>
        </p>
      )}

      {lines.length > 0 ? (
        <CodeBlock
          className={styles.logs}
          code={stripLogOffsets(payload.logs)}
          language={`output · ${String(lines.length)} ${lines.length === 1 ? "line" : "lines"}`}
          headerActions={
            payload.logsTruncated && (
              <StatusBadge
                tone="warning"
                icon={Scissors}
                label="Logs truncated"
                tooltip="Some lines were dropped inside the sandbox to stay within its log buffer, and cannot be recovered."
              />
            )
          }
          highlightedChildren={<LogLines lines={lines} />}
        />
      ) : (
        <p className={styles.empty}>The run printed nothing.</p>
      )}
    </div>
  );
}

function LogLines({ lines }: { lines: LogLine[] }): React.JSX.Element {
  return (
    <code className={styles.logLines}>
      {lines.map((line, index) => (
        <span
          // Lines have no identity beyond their position, and the list never reorders.
          key={index}
          className={classNames(styles.logLine, LINE_CLASS[line.kind])}
          data-kind={line.kind}
        >
          <span
            className={styles.offset}
            aria-hidden
            title={line.offset ?? undefined}
          >
            {line.showOffset ? line.offset : ""}
          </span>
          <LineText text={line.text} />
        </span>
      ))}
    </code>
  );
}

/** Leading whitespace in its own unwrappable box, so a long line wraps under its indentation. */
function LineText({ text }: { text: string }): React.JSX.Element {
  const body = text.trimStart();
  const indent = text.slice(0, text.length - body.length);
  return (
    <span className={styles.lineText}>
      {indent && <span className={styles.indent}>{indent}</span>}
      <span className={styles.lineBody}>{body}</span>
    </span>
  );
}
